import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { crm, ApiError, type CustomerStatement } from "../lib/api";
import { money, uuid } from "../lib/utils";

// Clientes: lista (from('customers') con RLS) + ficha con estado de cuenta
// (customer_statement RPC) + abono (credit_payment_apply, idempotente).
export const Route = createFileRoute("/clientes")({ component: Clientes });

interface CustomerRow { id: string; name: string; tax_receiver_type: string; phone: string | null }

function Clientes() {
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const { data: customers, isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase.from("customers").select("id, name, tax_receiver_type, phone").eq("is_active", true).order("name");
      if (error) throw error;
      return (data ?? []) as CustomerRow[];
    },
  });

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_420px]">
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Clientes</h1>
          <button onClick={() => setCreating(true)} className="btn btn-primary">+ Nuevo cliente</button>
        </div>
        {creating && <NewCustomer onDone={() => setCreating(false)} onCancel={() => setCreating(false)} />}
        <div className="overflow-hidden rounded-xl border bg-white">
          {isLoading ? <div className="p-8 text-center text-grafito-400">Cargando…</div>
            : (customers ?? []).map((c) => (
              <button key={c.id} onClick={() => setSelected(c.id)}
                className={`flex w-full items-center justify-between border-b px-4 py-3 text-left last:border-0 hover:bg-grafito-50 ${selected === c.id ? "bg-brand-50" : ""}`}>
                <div><div className="font-medium">{c.name}</div><div className="text-xs text-grafito-400">{c.phone ?? "—"}</div></div>
                <span className="text-xs uppercase text-grafito-400">{c.tax_receiver_type}</span>
              </button>
            ))}
        </div>
      </div>
      {selected && <CustomerDetail customerId={selected} />}
    </div>
  );
}

function CustomerDetail({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const [abono, setAbono] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["statement", customerId],
    queryFn: () => crm.statement(customerId),
  });

  const pay = useMutation({
    mutationFn: (amount: number) => crm.applyPayment(customerId, amount, uuid()),
    onSuccess: () => { setAbono(""); setMsg("Abono aplicado."); void qc.invalidateQueries({ queryKey: ["statement", customerId] }); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  if (isLoading) return <aside className="rounded-xl border bg-white p-6 text-grafito-400">Cargando…</aside>;
  if (!data) return <aside className="rounded-xl border bg-white p-6 text-grafito-400">No se pudo cargar el estado de cuenta.</aside>;
  const s = data as CustomerStatement;
  const credit = s.credit as null | { balance: number; available: number; limit: number; status: string };

  return (
    <aside className="rounded-xl border bg-white p-6">
      <h2 className="mb-4 text-lg font-bold">Estado de cuenta</h2>
      {!credit ? (
        <p className="text-grafito-400">Sin línea de crédito.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Stat label="Saldo" value={money(credit.balance)} accent />
            <Stat label="Disponible" value={money(credit.available)} />
            <Stat label="Límite" value={money(credit.limit)} />
            <Stat label="Estado" value={credit.status} />
          </div>

          <div className="mt-4">
            <div className="mb-1 text-xs font-semibold text-grafito-400">Antigüedad de saldo</div>
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(s.aging ?? {}).map(([b, v]) => (
                <span key={b} className="rounded bg-grafito-100 px-2 py-1">{b}: {money(v)}</span>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <label className="mb-1 block text-sm font-medium">Registrar abono</label>
            <div className="flex gap-2">
              <input type="number" step="0.01" min="0" value={abono} onChange={(e) => setAbono(e.target.value)}
                placeholder="0.00" className="flex-1 rounded-lg border px-3 py-2 text-right outline-none focus:border-brand" />
              <button onClick={() => pay.mutate(parseFloat(abono))} disabled={!abono || pay.isPending}
                className="btn btn-primary disabled:opacity-40">Abonar</button>
            </div>
            {msg && <p className="mt-2 text-sm text-grafito-400">{msg}</p>}
          </div>
        </>
      )}
    </aside>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-grafito-400">{label}</div>
      <div className={`font-semibold ${accent ? "text-brand" : ""}`}>{value}</div>
    </div>
  );
}

// Alta de cliente. Tipo receptor fiscal define si RUC+DV es obligatorio (lo
// fuerza también el CHECK chk_ruc_required y el guard del RPC customer_upsert).
function NewCustomer({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", taxReceiverType: "consumidor_final", segment: "minorista", ruc: "", dv: "", phone: "", creditLimit: "" });
  const [msg, setMsg] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const needsRuc = f.taxReceiverType === "contribuyente" || f.taxReceiverType === "gobierno";

  const save = useMutation({
    mutationFn: async () => {
      const c = await crm.upsertCustomer({
        name: f.name, taxReceiverType: f.taxReceiverType, commercialSegment: f.segment,
        ruc: needsRuc ? f.ruc : undefined, dv: needsRuc ? f.dv : undefined, phone: f.phone || undefined,
      });
      if (f.creditLimit && parseFloat(f.creditLimit) > 0) {
        await crm.setCreditAccount(c.id, parseFloat(f.creditLimit));
      }
      return c;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["customers"] }); onDone(); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div className="mb-4 rounded-xl border bg-white p-4">
      {msg && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{msg}</div>}
      <div className="grid grid-cols-2 gap-3">
        <input value={f.name} onChange={set("name")} placeholder="Nombre / razón social" className="col-span-2 rounded-lg border px-3 py-2" />
        <select value={f.taxReceiverType} onChange={set("taxReceiverType")} className="rounded-lg border px-3 py-2">
          <option value="consumidor_final">Consumidor final</option>
          <option value="contribuyente">Contribuyente</option>
          <option value="gobierno">Gobierno</option>
          <option value="extranjero">Extranjero</option>
        </select>
        <select value={f.segment} onChange={set("segment")} className="rounded-lg border px-3 py-2">
          <option value="minorista">Minorista</option><option value="mayorista">Mayorista</option>
          <option value="obra">Obra</option><option value="vip">VIP</option>
        </select>
        {needsRuc && <>
          <input value={f.ruc} onChange={set("ruc")} placeholder="RUC" className="rounded-lg border px-3 py-2" />
          <input value={f.dv} onChange={set("dv")} placeholder="DV" className="rounded-lg border px-3 py-2" />
        </>}
        <input value={f.phone} onChange={set("phone")} placeholder="Teléfono" className="rounded-lg border px-3 py-2" />
        <input value={f.creditLimit} onChange={set("creditLimit")} type="number" placeholder="Límite de crédito (opcional)" className="rounded-lg border px-3 py-2" />
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={onCancel} className="btn btn-ghost flex-1">Cancelar</button>
        <button onClick={() => save.mutate()} disabled={!f.name.trim() || save.isPending} className="btn btn-primary flex-1 disabled:opacity-40">
          {save.isPending ? "Guardando…" : "Crear cliente"}
        </button>
      </div>
    </div>
  );
}
